import { useState, useEffect } from 'react';
import { getCurrentCompanyConfig, CompanyConfig } from '@/config/companies';
import { getBaseUrl } from '@/utils/basePath';

export interface TenantJsonConfig {
  name: string;
  apiUrl?: string;
  logo?: string;
  favicon?: string;
  supportEmail?: string;
  aspAccreditationNumber?: string;
  aspName?: string;
  serviceProviderId?: string;
  smpAccesspointId?: string;
  features?: Record<string, boolean>;
  primaryColor?: string;
}

export function useTenantConfig() {
  const [data, setData] = useState<TenantJsonConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const company = getCurrentCompanyConfig();

  useEffect(() => {
    let isMounted = true;
    
    fetch(company.configPath)
      .then((res) => {
        if (!res.ok) throw new Error('Tenant config not found');
        return res.json();
      })
      .then((json) => {
        if (isMounted) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err);
          setLoading(false);
          // Fallback to name from derived config if JSON fails
          setData({ name: company.name });
        }
      });

    return () => { isMounted = false; };
  }, [company.configPath, company.name]);

  const base = getBaseUrl();
  const getNormalizedPath = (pathStr?: string) => {
    if (!pathStr) return undefined;
    if (pathStr.startsWith('/') && !pathStr.startsWith(base)) {
      return `${base}${pathStr.slice(1)}`;
    }
    return pathStr;
  };

  const mergedConfig = {
    ...company,
    name: data?.name || company.name,
    apiUrl: data?.apiUrl || company.apiUrl,
    logo: getNormalizedPath(data?.logo) || company.logo,
    favicon: getNormalizedPath(data?.favicon) || company.favicon,
    aspAccreditationNumber: data?.aspAccreditationNumber || company.aspAccreditationNumber,
    aspName: data?.aspName || company.aspName,
    serviceProviderId: data?.serviceProviderId || company.serviceProviderId,
    smpAccesspointId: data?.smpAccesspointId || company.smpAccesspointId,
    supportEmail: data?.supportEmail || company.supportEmail,
  };

  return { 
    tenantConfig: data, 
    baseConfig: company,
    mergedConfig, 
    loading, 
    error 
  };
}

